export interface IMentionable {
  userId: string;
  name: string;
}

export interface IMentionSegment {
  type: 'text' | 'mention';
  text: string;
  userId?: string;
}

const TOKEN = /<@(\d+)>/g;
const QUERY = /(^|\s)@([^\s@]*)$/;
const WORD = /\w/;

export function encodeMentions(text: string, mentions: IMentionable[]): string {
  if (mentions.length === 0) {
    return text;
  }

  const idsByName = new Map<string, string[]>();

  for (const mention of mentions) {
    const ids = idsByName.get(mention.name);

    if (ids) {
      ids.push(mention.userId);
    } else {
      idsByName.set(mention.name, [mention.userId]);
    }
  }

  const names = [...idsByName.keys()].sort((a, b) => b.length - a.length);
  let body = '';
  let index = 0;

  while (index < text.length) {
    const name =
      text[index] === '@'
        ? names.find(
            (candidate) =>
              text.startsWith(candidate, index + 1) &&
              !WORD.test(text[index + 1 + candidate.length] ?? ''),
          )
        : undefined;

    if (name === undefined) {
      body += text[index];
      index += 1;
      continue;
    }

    const ids = idsByName.get(name) as string[];
    const userId = (ids.length > 1 ? ids.shift() : ids[0]) as string;

    body += `<@${userId}>`;
    index += 1 + name.length;
  }

  return body;
}

export function mentionQueryAt(
  text: string,
  caret: number,
): { query: string; start: number } | undefined {
  const match = QUERY.exec(text.slice(0, caret));

  if (!match) {
    return undefined;
  }

  const query = match[2] ?? '';

  return { query, start: caret - query.length - 1 };
}

export function segmentMentions(
  body: string,
  names: Map<string, string>,
  fallback: string,
): IMentionSegment[] {
  const segments: IMentionSegment[] = [];
  let last = 0;

  for (const match of body.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    const userId = match[1] as string;

    if (index > last) {
      segments.push({ type: 'text', text: body.slice(last, index) });
    }

    segments.push({
      type: 'mention',
      text: `@${names.get(userId) ?? fallback}`,
      userId,
    });
    last = index + match[0].length;
  }

  if (last < body.length) {
    segments.push({ type: 'text', text: body.slice(last) });
  }

  return segments;
}

export function filterMentionables(
  mentionables: IMentionable[],
  query: string,
  limit = 6,
): IMentionable[] {
  const needle = query.trim().toLowerCase();

  return mentionables
    .filter((mention) => mention.name.toLowerCase().includes(needle))
    .slice(0, limit);
}

interface IRosterSource {
  members?: { userId?: string | null; name?: string | null }[];
  teammates?: { id?: string; userId?: string | null; name?: string | null }[];
  currentUserId?: string;
}

export function mentionablesFrom({
  members = [],
  teammates = [],
  currentUserId,
}: IRosterSource): IMentionable[] {
  const byUserId = new Map<string, IMentionable>();

  for (const member of members) {
    if (member.userId && member.name) {
      byUserId.set(member.userId, { userId: member.userId, name: member.name });
    }
  }

  for (const teammate of teammates) {
    if (teammate.userId && teammate.name && !teammate.id?.startsWith('self-')) {
      byUserId.set(teammate.userId, {
        userId: teammate.userId,
        name: teammate.name,
      });
    }
  }

  return [...byUserId.values()].filter(
    (mention) => mention.userId !== currentUserId,
  );
}
